"use client"

import { useEffect, useMemo, useState } from "react"
import { useRegion } from "./use-region"
import { buildRegionAwareHref } from "../lib/utils"
import { useSession } from "@/features/auth/context/session.context"

export function useRegionAwareHref(url: string) {
  const { regionSlug } = useRegion()
  const { profile } = useSession()
  const [fallbackSlug, setFallbackSlug] = useState<string | null>(null)
  const [resolvedProfileRegion, setResolvedProfileRegion] = useState<{ id: number; slug: string | null } | null>(null)

  const profileRegionId = typeof profile?.selectedRegion === 'number' ? profile.selectedRegion : null

  useEffect(() => {
    let cancelled = false

    if (regionSlug) {
      setFallbackSlug(regionSlug)
      return
    }

    if (profileRegionId === null) {
      setFallbackSlug(null)
      return
    }

    if (resolvedProfileRegion && resolvedProfileRegion.id === profileRegionId) {
      setFallbackSlug(resolvedProfileRegion.slug ?? null)
      return
    }

    const resolveSlug = async () => {
      try {
        const response = await fetch(`/api/regions/${profileRegionId}`)
        if (!response.ok) {
          throw new Error(`Failed to fetch region ${profileRegionId}`)
        }
        const payload = await response.json()
        const regionData = payload.success ? payload.data : payload
        if (!cancelled) {
          const slug = regionData?.slug ?? null
          setResolvedProfileRegion({ id: profileRegionId, slug })
          setFallbackSlug(slug)
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Unable to resolve region slug from profile selection', error)
          setResolvedProfileRegion({ id: profileRegionId, slug: null })
          setFallbackSlug(null)
        }
      }
    }

    resolveSlug()

    return () => {
      cancelled = true
    }
  }, [regionSlug, profileRegionId, resolvedProfileRegion])

  const effectiveSlug = regionSlug ?? fallbackSlug ?? null

  return useMemo(() => buildRegionAwareHref(effectiveSlug, url), [effectiveSlug, url])
}
